import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet, useNavigate, Navigate, useLocation } from 'react-router-dom';
import posthog from 'posthog-js';
import { SignedIn, SignedOut, useAuth, useUser } from '@clerk/clerk-react';
import { Toaster } from './components/ui/sonner';
const V2App = React.lazy(() => import('./v2/V2App'));
const V2_VIEWS = ['chat', 'today', 'agents', 'team', 'prefs'];
const PricingPage = React.lazy(() => import('./components/pages/PricingPage'));
const TryPage = React.lazy(() => import('./components/pages/TryPage'));
const NotFoundPage = React.lazy(() => import('./components/pages/NotFoundPage'));
const AppNotFoundPage = React.lazy(() => import('./components/pages/AppNotFoundPage'));
const AdminDashboard = React.lazy(() => import('./components/dashboard/AdminDashboard'));
const PaymentSuccess = React.lazy(() => import('./components/dashboard/PaymentPages').then(m => ({ default: m.PaymentSuccess })));
const PaymentCancel = React.lazy(() => import('./components/dashboard/PaymentPages').then(m => ({ default: m.PaymentCancel })));
import LandingPage from './components/LandingPage';
import AuthScreen from './components/AuthScreen';
import { TendLogoIcon } from './components/ui/TendLogo';
import ErrorBoundary from './components/ui/ErrorBoundary';
const PrivacyPolicy = React.lazy(() => import('./components/pages/PrivacyPolicy'));
const TermsConditions = React.lazy(() => import('./components/pages/TermsConditions'));
const Contact = React.lazy(() => import('./components/pages/Contact'));
const JoinPage = React.lazy(() => import('./components/pages/Receive').then((m) => ({ default: m.JoinPage })));
const SharedHelperPage = React.lazy(() => import('./components/pages/Receive').then((m) => ({ default: m.SharedHelperPage })));
const AppsPage = React.lazy(() => import('./components/pages/About').then((m) => ({ default: m.AppsPage })));
const TrustPage = React.lazy(() => import('./components/pages/About').then((m) => ({ default: m.TrustPage })));
const WhatsNewPage = React.lazy(() => import('./components/pages/About').then((m) => ({ default: m.WhatsNewPage })));
const Onboarding = React.lazy(() => import('./v2/Onboarding'));

// Route loader component for Suspense fallback
const RouteLoader = () => (
  <div className="tend-loader" aria-label="Loading">
    <TendLogoIcon className="h-10 w-8" color="currentColor" />
  </div>
);
import { Loader2, Check, Leaf } from 'lucide-react';
import { toast } from 'sonner';
import { subscribeToPush, checkAndRefreshSubscription } from './utils/pushNotifications';
import { setAuthTokenGetter } from './services/api';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Reusable protected route layout — renders children only when signed in
const ProtectedLayout = () => {
  const { isLoaded, isSignedIn } = useAuth();
  const location = useLocation();
  if (!isLoaded) return <RouteLoader />;
  if (!isSignedIn) {   // invites and shared helpers must survive sign-in
    if (location.search) { try { sessionStorage.setItem('tend.afterAuth', location.pathname + location.search); } catch { /* private mode */ } }
    return <Navigate to="/login" replace />;
  }
  try { sessionStorage.removeItem('tend.afterAuth'); } catch { /* private mode */ }
  return <Outlet />;
};

// Derive current view + settings tab from the URL pathname
const getViewFromPath = (pathname) => {
  const parts = pathname.split('/').filter(Boolean);
  // /app → chat, /app/tasks → tasks, /app/settings/notifications → settings + tab
  const view = parts[1] || 'today';
  const tab = parts[2] || null;
  return { view, tab };
};

// Main app wrapper — view driven entirely by URL
const AppContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { view, tab } = getViewFromPath(location.pathname);

  const [needsOnboarding, setNeedsOnboarding] = useState(null);
  const [loading, setLoading] = useState(true);
  const { getToken } = useAuth();
  const { user } = useUser();

  // Must be declared here (before any early returns) — hooks cannot be conditional

  // Handle legacy ?settings= query param (used by OAuth integration callbacks)
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const settingsParam = urlParams.get('settings');
    if (settingsParam) {
      navigate('/app/prefs', { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set auth token getter
  useEffect(() => {
    setAuthTokenGetter(getToken);
  }, [getToken]);

  // Auto-connect Chrome extension — silently fires TendConnectExtension whenever
  // the user is signed in and the extension is installed (no manual step required).
  // Session-level guard prevents this from re-firing on every getToken-ref change
  // (Clerk's useAuth can return a new function ref between renders, which would
  // otherwise trigger N duplicate POST /api/users/api-keys calls).
  useEffect(() => {
    let cancelled = false;
    if (sessionStorage.getItem('tend_extension_connected') === 'true') return;
    const autoConnectExtension = async () => {
      if (!document.documentElement.hasAttribute('data-tend-extension-installed')) return;
      try {
        const token = await getToken();
        // Reuse an existing active key to avoid creating a new one each page load
        const listRes = await axios.get(`${API}/api/users/api-keys`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        let key = (listRes.data.api_keys || []).find(k => k.active)?.key;
        if (!key) {
          const createRes = await axios.post(`${API}/api/users/api-keys`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          key = createRes.data.api_key?.key;
        }
        if (!key || cancelled) return;
        sessionStorage.setItem('tend_extension_connected', 'true');
        window.dispatchEvent(new CustomEvent('TendConnectExtension', {
          detail: {
            apiKey: key,
            apiBase: process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001',
          }
        }));
      } catch (_) { /* silently ignore — extension connection is best-effort */ }
    };
    autoConnectExtension();
    return () => { cancelled = true; };
  }, [getToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check if user needs onboarding — runs once per session.
  // Guard via ran-flag prevents re-fire when getToken ref changes (Clerk v5
  // useAuth can return new function ref between renders, which previously
  // caused 30+ duplicate /api/users/me + PUT timezone calls per page load).
  const onboardingCheckedRef = useRef(false);
  useEffect(() => {
    if (onboardingCheckedRef.current) return;
    onboardingCheckedRef.current = true;

    // Hard timeout — if checkOnboarding hangs for any reason (Clerk slow,
    // backend unreachable, network stall), flip loading=false after 8s so
    // user is never stuck on splash forever. App falls back to non-onboarded
    // mode and TendChat handles missing data gracefully.
    const hardTimeout = setTimeout(() => {
      console.warn('[App] checkOnboarding hard-timeout reached — proceeding without user data');
      setNeedsOnboarding(false);
      setLoading(false);
    }, 8000);

    const checkOnboarding = async () => {
      // Session-level guard: if user already finished onboarding this session, never re-show it
      if (sessionStorage.getItem('tend_onboarding_done') === 'true') {
        clearTimeout(hardTimeout);
        setNeedsOnboarding(false);
        setLoading(false);
        return;
      }

      try {
        const token = await getToken();
        const response = await axios.get(`${API}/api/users/me`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 6000,  // axios-level timeout — never hang on this fetch
        });

        // New user who hasn't completed onboarding
        const userData = response.data;

        // Auto-update timezone if strictly different (only once per session)
        const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
        if (
          userData.timezone !== detectedTimezone &&
          sessionStorage.getItem('tend_tz_synced') !== detectedTimezone
        ) {
          console.log(`Updating timezone from ${userData.timezone} to ${detectedTimezone}`);
          // Fire-and-forget — don't block splash on the timezone PUT
          axios.put(`${API}/api/users/me`,
            { timezone: detectedTimezone },
            { headers: { Authorization: `Bearer ${token}` }, timeout: 5000 }
          ).then(() => {
            sessionStorage.setItem('tend_tz_synced', detectedTimezone);
          }).catch(() => { /* non-critical */ });
        }

        if (userData.is_new_user || !userData.onboarding_completed) {
          setNeedsOnboarding(true);
        } else {
          setNeedsOnboarding(false);
          checkAndRefreshSubscription(token);
        }
      } catch (error) {
        console.error('Error checking onboarding:', error);
        setNeedsOnboarding(false);
      } finally {
        clearTimeout(hardTimeout);
        setLoading(false);
      }
    };

    checkOnboarding();
  }, [getToken]);

  const handleNavigate = (newView, newTab = null) => {
    if (newView === 'settings') {
      navigate(newTab ? `/app/settings/${newTab}` : '/app/settings');
    } else {
      navigate(`/app/${newView}`);
    }
  };

  const handleOnboardingComplete = () => {
    sessionStorage.setItem('tend_onboarding_done', 'true');
    setNeedsOnboarding(false);
  };

  if (loading) {
    return (
      <RouteLoader />
    );
  }

  if (needsOnboarding) {
    return (
      <React.Suspense fallback={<RouteLoader />}>
        <Onboarding onComplete={handleOnboardingComplete} user={user} />
      </React.Suspense>
    );
  }

  // /app with no sub-path: Today is home
  if (!view || view === 'app') {
    return <Navigate to="/app/today" replace />;
  }
  const LEGACY = { tasks: 'today', calendar: 'today', missions: 'agents', goals: 'agents', inbox: 'chat', workspace: 'team', settings: 'prefs' };
  if (LEGACY[view]) {
    return <Navigate to={`/app/${LEGACY[view]}${location.search}`} replace />;
  }
  if (V2_VIEWS.includes(view)) {
    return <React.Suspense fallback={<RouteLoader />}><V2App view={view} /></React.Suspense>;
  }

  return (
    <React.Suspense fallback={<RouteLoader />}>
      {view === 'pricing' && (
        <PricingPage />
      )}
    </React.Suspense>
  );
};

// Payment route wrapper
const PaymentSuccessWrapper = () => {
  const navigate = useNavigate();
  return <PaymentSuccess onContinue={() => navigate('/app')} />;
};

const PaymentCancelWrapper = () => {
  const navigate = useNavigate();
  return <PaymentCancel onContinue={() => navigate('/app')} />;
};

const AdminWrapper = () => {
  const navigate = useNavigate();
  return <AdminDashboard onBack={() => navigate('/app')} />;
};

// SEO Dashboard wrapper

// Admin Roadmap wrapper

// PostHog Pageview Tracker for SPA
const PostHogTracker = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    if (process.env.REACT_APP_POSTHOG_KEY) {
      posthog.capture('$pageview');
    }
  }, [location]);

  return null;
};

import { HelmetProvider } from 'react-helmet-async';

function App() {
  // Service Worker Update Listener
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        window.location.reload();
      });

      navigator.serviceWorker.ready.then(registration => {
        if (registration.waiting) {
          toast('Update available', {
            description: 'A new version of Tend is ready.',
            action: {
              label: 'Reload',
              onClick: () => {
                registration.waiting.postMessage({ type: 'SKIP_WAITING' });
              }
            },
            duration: Infinity
          });
        }

        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                toast('Update available', {
                  description: 'A new version of Tend is ready.',
                  action: {
                    label: 'Reload',
                    onClick: () => {
                      newWorker.postMessage({ type: 'SKIP_WAITING' });
                    }
                  },
                  duration: Infinity
                });
              }
            });
          }
        });
      });
    }
  }, []);

  return (
    <HelmetProvider>
    <Router>
      <PostHogTracker />
      <ErrorBoundary>
      <React.Suspense fallback={<RouteLoader />}>
      <Routes>
        {/* Public Routes - Always accessible */}
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsConditions />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/join/:token" element={<JoinPage />} />
        <Route path="/h/:token" element={<SharedHelperPage />} />
        <Route path="/apps" element={<AppsPage />} />
        <Route path="/trust" element={<TrustPage />} />
        <Route path="/whats-new" element={<WhatsNewPage />} />

        {/* Auth Routes */}
        <Route path="/login" element={
          <>
            <SignedOut>
              <AuthScreen mode="signin" />
            </SignedOut>
            <SignedIn>
              <Navigate to="/app" replace />
            </SignedIn>
          </>
        } />
        <Route path="/signup" element={
          <>
            <SignedOut>
              <AuthScreen mode="signup" />
            </SignedOut>
            <SignedIn>
              <Navigate to="/app" replace />
            </SignedIn>
          </>
        } />

        {/* Protected App Routes — all require auth, view driven by URL */}
        <Route path="/app" element={<ProtectedLayout />}>
          <Route index element={<AppContent />} />
          <Route path="chat" element={<AppContent />} />
          <Route path="tasks" element={<AppContent />} />
          <Route path="calendar" element={<AppContent />} />
          <Route path="workspace" element={<AppContent />} />
          <Route path="inbox" element={<AppContent />} />
          <Route path="missions" element={<AppContent />} />
          <Route path="today" element={<AppContent />} />
          <Route path="agents" element={<AppContent />} />
          <Route path="goals" element={<AppContent />} />
          <Route path="prefs" element={<AppContent />} />
          <Route path="team" element={<AppContent />} />
          <Route path="settings" element={<AppContent />} />
          <Route path="settings/:tab" element={<AppContent />} />
          <Route path="pricing" element={<AppContent />} />
          <Route path="*" element={<AppNotFoundPage />} />
        </Route>

        {/* Legacy redirects */}
        <Route path="/dashboard" element={<Navigate to="/app/settings" replace />} />
        <Route path="/calendar" element={<Navigate to="/app/today" replace />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/try" element={<TryPage />} />
        <Route path="/payment/success" element={
          <><SignedIn><PaymentSuccessWrapper /></SignedIn><SignedOut><Navigate to="/login" replace /></SignedOut></>
        } />
        <Route path="/payment/cancel" element={
          <><SignedIn><PaymentCancelWrapper /></SignedIn><SignedOut><Navigate to="/login" replace /></SignedOut></>
        } />
        <Route path="/admin" element={
          <><SignedIn><AdminWrapper /></SignedIn><SignedOut><Navigate to="/login" replace /></SignedOut></>
        } />

        {/* Landing Page - Default Route */}
        <Route path="/" element={
          <>
            <SignedOut>
              <LandingPage />
            </SignedOut>
            <SignedIn>
              <Navigate to="/app" replace />
            </SignedIn>
          </>
        } />

        {/* Catch all - 404 page */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </React.Suspense>
      </ErrorBoundary>

      <Toaster position="bottom-right" theme="system"
        toastOptions={{ style: { borderRadius: '12px', fontFamily: 'inherit', fontSize: '14px' }, className: 'toast-notification' }} />

      {/* Responsive toast positioning via CSS */}
      <style>{`
        /* Desktop: Bottom-right, premium glass */
        @media (min-width: 768px) {
          [data-sonner-toaster] {
            right: 24px !important;
            left: auto !important;
            bottom: 32px !important;
            top: auto !important;
          }
          [data-sonner-toaster] [data-sonner-toast] {
            min-width: 300px;
            max-width: 380px;
          }
        }
        
        /* Mobile: Top center, clear of header */
        @media (max-width: 767px) {
          [data-sonner-toaster] {
            top: max(80px, env(safe-area-inset-top, 80px)) !important;
            bottom: auto !important;
            left: 50% !important;
            right: auto !important;
            transform: translateX(-50%) !important;
            width: 92% !important;
            max-width: 400px !important;
          }
          [data-sonner-toaster] [data-sonner-toast] {
            width: 100% !important;
            font-size: 14px;
            padding: 16px;
            border-radius: 16px;
            box-shadow: 0 10px 40px rgba(0,0,0,0.15);
            color: #1C1C1C !important;
            border: 1px solid rgba(0,0,0,0.05);
          }
          
          /* Prevent page wobble on mobile */
          .h-screen {
            overflow-x: hidden !important;
            width: 100vw;
          }
        }
      `}</style>
    </Router>
    </HelmetProvider>
  );
}

export default App;
